import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Flame, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n";
import { BACKEND_URL, TURNSTILE_SITE_KEY } from "@/lib/env";

const API = `${BACKEND_URL}/api`;

const LANG_LS_KEY = "persona-dm-lang";

function loadTurnstileScript() {
  if (typeof window === "undefined") return;
  if (window.turnstile || document.getElementById("cf-turnstile-script")) return;
  const s = document.createElement("script");
  s.id = "cf-turnstile-script";
  s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

export default function Register() {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(LANG_LS_KEY) || "sk"; } catch { return "sk"; }
  });
  const t = useT(lang);
  const nav = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const widgetRef = useRef(null);
  const widgetId = useRef(null);
  const [tsToken, setTsToken] = useState("");

  useEffect(() => {
    loadTurnstileScript();
    let interval = setInterval(() => {
      if (window.turnstile && widgetRef.current && widgetId.current === null) {
        widgetId.current = window.turnstile.render(widgetRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "dark",
          callback: (tok) => setTsToken(tok || ""),
          "error-callback": () => setTsToken(""),
          "expired-callback": () => setTsToken(""),
        });
        clearInterval(interval);
      }
    }, 250);
    return () => clearInterval(interval);
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !email.trim() || !password || !reason.trim()) {
      toast.error(t("registerFillAll"));
      return;
    }
    if (password.length < 8) {
      toast.error(t("registerPwShort"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/register`, {
        name: name.trim(),
        email: email.trim(),
        password,
        reason: reason.trim(),
        honeypot,
        turnstileToken: tsToken,
      });
      setDone(true);
      toast.success(t("registerSuccess"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || t("errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="cockpit-bg min-h-screen w-full flex items-center justify-center px-4" data-testid="register-success">
        <div className="w-full max-w-md surface p-8 text-center">
          <div className="w-12 h-12 rounded-md bg-[#FF006E]/15 border border-[#FF006E]/40 mx-auto mb-4 flex items-center justify-center">
            <Flame size={22} strokeWidth={1.7} className="text-[#FF006E]" />
          </div>
          <h1 className="font-display text-xl font-semibold mb-2">{t("registerDoneTitle")}</h1>
          <p className="text-zinc-400 text-sm leading-relaxed mb-6">{t("registerDoneBody")}</p>
          <button
            type="button"
            onClick={() => nav("/")}
            className="cta-primary rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em]"
            data-testid="register-goto-login-btn"
          >
            {t("registerGotoLogin")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cockpit-bg min-h-screen w-full flex items-center justify-center px-4 py-10" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#FF006E]/15 border border-[#FF006E]/40 flex items-center justify-center">
              <Flame size={18} strokeWidth={1.75} className="text-[#FF006E]" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold tracking-tight">{t("appTitle")}</h1>
              <p className="text-xs text-zinc-400">{t("registerSubtitle")}</p>
            </div>
          </div>
          <div className="pill-group">
            <button type="button" className={`pill-btn ${lang === "sk" ? "active" : ""}`}
              onClick={() => { setLang("sk"); try { localStorage.setItem(LANG_LS_KEY, "sk"); } catch {} }}
              data-testid="reg-lang-sk">SK</button>
            <button type="button" className={`pill-btn ${lang === "en" ? "active" : ""}`}
              onClick={() => { setLang("en"); try { localStorage.setItem(LANG_LS_KEY, "en"); } catch {} }}
              data-testid="reg-lang-en">EN</button>
          </div>
        </div>

        <form onSubmit={submit} className="surface p-7 space-y-4" data-testid="register-form">
          <div className="mb-2 overline">{t("registerOverline")}</div>
          <h2 className="font-display text-lg font-semibold mb-4">{t("registerTitle")}</h2>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2 block">{t("registerName")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Meno Priezvisko" className="field-input bg-transparent" data-testid="reg-name-input" maxLength={80} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2 block">{t("emailLabel")}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" className="field-input bg-transparent" data-testid="reg-email-input" maxLength={200} />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2 block">{t("passwordLabel")}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min. 8 znakov" autoComplete="new-password" className="field-input bg-transparent" data-testid="reg-password-input" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2 block">{t("registerReason")}</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("registerReasonPh")} className="field-textarea bg-transparent min-h-[90px]" data-testid="reg-reason-input" maxLength={1000} />
          </div>

          {/* Honeypot — hidden from users */}
          <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }} aria-hidden="true">
            <input tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} placeholder="Leave empty" data-testid="reg-honeypot" />
          </div>

          {/* Turnstile widget */}
          <div ref={widgetRef} className="mt-2" data-testid="reg-turnstile" />

          <button type="submit" disabled={loading}
            className="cta-primary rounded-md px-5 py-3 text-sm font-semibold uppercase tracking-[0.14em] inline-flex items-center gap-2 w-full justify-center"
            data-testid="reg-submit-btn">
            {loading ? <span className="typing"><span /><span /><span /></span> : <><UserPlus size={16} strokeWidth={1.7} />{t("registerSubmit")}</>}
          </button>

          <p className="text-[11px] text-zinc-500 text-center pt-1">
            {t("registerAlreadyHave")}{" "}
            <Link to="/" className="text-[#FF006E] hover:text-[#ff338b]" data-testid="reg-goto-login">
              {t("loginBtn")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
